/* Namespaced, versioned localStorage.

   Everything the app persists goes through here, so a schema change is a
   version bump rather than a class of "old buffer, new code" bugs that would
   surface mid-exam. Reads and writes never throw: private mode, a full quota
   and a browser configured to block site data all present as a failure here,
   and none of them may take the exam down. */

const NAMESPACE = 'sm'
const SCHEMA_VERSION = 1

type Envelope<T> = {
  v: number
  at: number
  data: T
}

function key(name: string): string {
  return `${NAMESPACE}:v${SCHEMA_VERSION}:${name}`
}

export function read<T>(name: string): T | null {
  try {
    const raw = localStorage.getItem(key(name))
    if (raw === null) return null
    const envelope = JSON.parse(raw) as Envelope<T>
    if (envelope.v !== SCHEMA_VERSION) {
      localStorage.removeItem(key(name))
      return null
    }
    return envelope.data
  } catch {
    return null
  }
}

export function write<T>(name: string, data: T): boolean {
  try {
    const envelope: Envelope<T> = { v: SCHEMA_VERSION, at: Date.now(), data }
    localStorage.setItem(key(name), JSON.stringify(envelope))
    return true
  } catch {
    // Quota exceeded or storage blocked. The caller keeps its in-memory state,
    // which is still the source of truth; only crash recovery is lost.
    return false
  }
}

export function remove(name: string): void {
  try {
    localStorage.removeItem(key(name))
  } catch {
    /* nothing to do */
  }
}

/** When the envelope was last written, for staleness checks. */
export function writtenAt(name: string): number | null {
  try {
    const raw = localStorage.getItem(key(name))
    if (raw === null) return null
    return (JSON.parse(raw) as Envelope<unknown>).at ?? null
  } catch {
    return null
  }
}

/** Drop every key this app owns — used on logout. */
export function clearAll(): void {
  try {
    const prefix = `${NAMESPACE}:`
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k?.startsWith(prefix)) doomed.push(k)
    }
    for (const k of doomed) localStorage.removeItem(k)
  } catch {
    /* nothing to do */
  }
}

/** Drop keys left behind by an older schema version. Called once on boot. */
export function pruneOldVersions(): void {
  try {
    const current = `${NAMESPACE}:v${SCHEMA_VERSION}:`
    const doomed: string[] = []
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i)
      if (k?.startsWith(`${NAMESPACE}:`) && !k.startsWith(current)) doomed.push(k)
    }
    for (const k of doomed) localStorage.removeItem(k)
  } catch {
    /* nothing to do */
  }
}

export const storageKeys = {
  examBuffer: (attemptId: number) => `exam:${attemptId}`,
  locale: 'locale',
} as const
